import { defineStore } from "pinia";
import { computed, ref } from "vue";

import type { Master } from "@/entities/master";
import { masterApi } from "@/entities/master";

/** Стан завантаження списку (spec §5.1). */
export type ListStatus = "idle" | "loading" | "ready" | "error";

/**
 * Стор списку майстрів (spec §5.1) — server-authoritative колекція. Setup-store
 * як `entities/session`/`services-catalog` (Δ Vuex: `ref`/`computed`/функції, без
 * mutations). Ownership — лише серверна колекція + її loading/error/empty; стану
 * форми/редагування тут НЕМАЄ (це composable `master-profile`, write-path).
 *
 * Інвалідація (S7): стор не мутується оптимістично. Після create/edit/delete
 * сторінка навігує на `/masters`, `MastersList` ре-маунтиться → `onMounted →
 * load()` → свіжий список із сервера.
 */
export const useMastersListStore = defineStore("masters-list", () => {
  const listStatus = ref<ListStatus>("idle");
  const masters = ref<Master[]>([]);
  /** Polite-озвучення success для скрін-рідера (spec §8.3), без toast-спаму. */
  const politeMessage = ref("");

  const isEmpty = computed(
    () => listStatus.value === "ready" && masters.value.length === 0,
  );

  /** `GET /api/masters` → loading → ready|error (retry). 401 — interceptor (ADR-0006 D4). */
  async function load(): Promise<void> {
    listStatus.value = "loading";
    try {
      masters.value = await masterApi.listMasters();
      listStatus.value = "ready";
    } catch {
      listStatus.value = "error";
    }
  }

  /**
   * Виставити polite-повідомлення (profile/create викликають перед навігацією на
   * список → live-region зачитає після маунту списку; write-path, інкремент B).
   */
  function announce(message: string): void {
    politeMessage.value = message;
  }

  return {
    listStatus,
    masters,
    politeMessage,
    isEmpty,
    load,
    announce,
  };
});
